'use client';

import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import type MapLocation from '@alltrails/amplitude/enums/MapLocation';
import logPrintMapButtonClicked from '@alltrails/amplitude/events/logPrintMapButtonClicked';
import useUser from '@alltrails/context/hooks/useUser';
import IconButton from '@alltrails/denali/components/IconButton';
import Print from '@alltrails/denali/icons/Print';
import usePrintModalExperiment from '@alltrails/experiments/hooks/usePrintModalExperiment';
import SignUpModal from '@alltrails/user-auth/components/SignUpModal';
import { useRedirectToPrintUrl } from '@/hooks/useRedirectToPrintUrl';
import { useAppSelector } from '@/utils/redux';

type LegacyPrintButtonProps = {
  mapLocation: MapLocation;
  trailId?: number;
};

/** Sends the user to the server-rendered print page. Replaced by PrintControl behind the experiment. */
const LegacyPrintButton = ({ mapLocation, trailId }: LegacyPrintButtonProps) => {
  const intl = useIntl();
  const user = useUser();
  const [isSignUpModalOpen, setIsSignUpModalOpen] = useState(false);
  const redirectToPrintUrl = useRedirectToPrintUrl(mapLocation === 'trail');
  const routeId = useAppSelector(state => state.customRoute?.route.id);
  const { sendExposure } = usePrintModalExperiment();

  const onPrintClick = useCallback(() => {
    logPrintMapButtonClicked({ map_location: mapLocation, trail_id: trailId ?? routeId });
    sendExposure();
    if (!user) {
      setIsSignUpModalOpen(true);
      return;
    }
    redirectToPrintUrl();
  }, [mapLocation, redirectToPrintUrl, routeId, sendExposure, trailId, user]);

  return (
    <>
      <IconButton
        elevated
        icon={{ Component: Print }}
        onClick={onPrintClick}
        title={intl.formatMessage({ defaultMessage: 'Print' })}
        variant="ghost"
      />
      {isSignUpModalOpen && (
        <SignUpModal
          isOpen={isSignUpModalOpen}
          closeModal={() => setIsSignUpModalOpen(false)}
          onSuccess={() => {
            setIsSignUpModalOpen(false);
            redirectToPrintUrl();
          }}
          trigger="Print Map"
          upsellTrigger="Print Map"
        />
      )}
    </>
  );
};

export default LegacyPrintButton;
